/**
 * QuizQuestion Component
 *
 * Renders a single quiz question with MC radio cards or T/F buttons.
 * Handles answer selection, immediate feedback display, and auto-advance.
 *
 * MC options: full-width tappable cards with radio indicator.
 * T/F buttons: two large buttons spanning the width.
 * Feedback: correct=green, incorrect=red, correct answer revealed + explanation.
 *
 * Accessibility:
 * - MC: role="radiogroup" container, role="radio" + aria-checked on each option.
 * - T/F: two <button> elements.
 * - Feedback area: aria-live="polite" for screen reader announcements.
 */

import { useEffect, useRef } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface QuizQuestionProps {
  /** Question text */
  questionText: string;
  /** Question number (1-based) */
  questionNumber: number;
  /** Whether this is a T/F question */
  isTrueFalse: boolean;
  /** Options for MC questions (shuffled); empty for T/F */
  options: string[];
  /** Currently selected answer (null if not yet answered) */
  selectedAnswer: string | null;
  /** Whether feedback is being shown */
  showFeedback: boolean;
  /** Whether the selected answer was correct (only valid when showFeedback=true) */
  isCorrect?: boolean;
  /** The correct answer text (only shown during feedback) */
  correctAnswer?: string;
  /** Explanation from observationGuidance (shown during feedback) */
  explanation?: string | null;
  /** Called when user selects an answer */
  onAnswer: (answer: string) => void;
}

export function QuizQuestion({
  questionText,
  questionNumber,
  isTrueFalse,
  options,
  selectedAnswer,
  showFeedback,
  isCorrect,
  correctAnswer,
  explanation,
  onAnswer,
}: QuizQuestionProps) {
  const feedbackRef = useRef<HTMLDivElement>(null);

  // Focus feedback area when it appears (for screen readers)
  useEffect(() => {
    if (showFeedback && feedbackRef.current) {
      feedbackRef.current.focus();
    }
  }, [showFeedback]);

  return (
    <div className="space-y-5" style={{ animation: 'fadeInUp 0.3s ease-out' }}>
      {/* Question text */}
      <div>
        <p className="text-xs font-medium text-op-text-tertiary">
          Question {questionNumber}
        </p>
        <p className="mt-2 text-lg font-semibold leading-snug text-op-text-primary">
          {questionText}
        </p>
      </div>

      {/* Answer options */}
      {isTrueFalse ? (
        <TrueFalseButtons
          selectedAnswer={selectedAnswer}
          showFeedback={showFeedback}
          correctAnswer={correctAnswer}
          onAnswer={onAnswer}
        />
      ) : (
        <MultipleChoiceOptions
          options={options}
          selectedAnswer={selectedAnswer}
          showFeedback={showFeedback}
          correctAnswer={correctAnswer}
          onAnswer={onAnswer}
        />
      )}

      {/* Feedback area — only rendered when grading result is available */}
      <div
        ref={feedbackRef}
        aria-live="polite"
        tabIndex={-1}
        className="outline-none"
      >
        {showFeedback && isCorrect !== undefined && (
          <div
            className={cn(
              'rounded-2xl p-4 transition-all duration-300',
              isCorrect
                ? 'bg-emerald-50 ring-1 ring-emerald-200'
                : 'bg-red-50 ring-1 ring-red-200'
            )}
            style={{ animation: 'fadeInUp 0.3s ease-out' }}
          >
            <div className="flex items-start gap-3">
              {isCorrect ? (
                <CheckCircle2 size={20} strokeWidth={1.5} className="mt-0.5 flex-shrink-0 text-emerald-600" />
              ) : (
                <XCircle size={20} strokeWidth={1.5} className="mt-0.5 flex-shrink-0 text-red-600" />
              )}
              <div className="min-w-0 flex-1">
                <p className={cn(
                  'text-sm font-semibold',
                  isCorrect ? 'text-emerald-700' : 'text-red-700'
                )}>
                  {isCorrect ? 'Correct!' : 'Incorrect'}
                </p>
                {!isCorrect && correctAnswer && (
                  <p className="mt-1 text-sm text-red-600">
                    The correct answer is: <span className="font-semibold">{correctAnswer}</span>
                  </p>
                )}
                {explanation && (
                  <p className="mt-2 text-sm leading-relaxed text-op-text-secondary">
                    {explanation}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// MC Options (radio cards)
// ============================================

function MultipleChoiceOptions({
  options,
  selectedAnswer,
  showFeedback,
  correctAnswer,
  onAnswer,
}: {
  options: string[];
  selectedAnswer: string | null;
  showFeedback: boolean;
  correctAnswer?: string;
  onAnswer: (answer: string) => void;
}) {
  return (
    <div role="radiogroup" aria-label="Answer options" className="space-y-3">
      {options.map((option, index) => {
        const isSelected = selectedAnswer === option;
        const isCorrectOption = showFeedback && option === correctAnswer;
        const isIncorrectSelected = showFeedback && isSelected && option !== correctAnswer;
        const disabled = showFeedback;

        // Option label (A, B, C, D)
        const label = String.fromCharCode(65 + index);

        return (
          <button
            key={`${option}-${index}`}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={`Option ${label}: ${option}`}
            disabled={disabled}
            onClick={() => !disabled && onAnswer(option)}
            className={cn(
              'flex w-full min-h-[48px] items-center gap-3 rounded-2xl px-4 py-3',
              'text-left text-sm font-medium transition-all duration-200',
              'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
              // Default state
              !isSelected && !showFeedback && 'bg-white ring-1 ring-gray-200 text-op-text-primary active:ring-2 active:ring-guild-green/50',
              // Selected (before feedback)
              isSelected && !showFeedback && 'ring-2 ring-guild-green bg-guild-green/5 text-op-text-primary',
              // Correct (feedback)
              isCorrectOption && 'ring-2 ring-emerald-500 bg-emerald-50 text-emerald-700',
              // Incorrect selected (feedback)
              isIncorrectSelected && 'ring-2 ring-red-500 bg-red-50 text-red-700',
              // Unselected during feedback (dim)
              showFeedback && !isCorrectOption && !isIncorrectSelected && 'bg-white ring-1 ring-gray-200 text-op-text-tertiary opacity-60',
              // Disabled cursor
              disabled && 'cursor-default',
            )}
          >
            {/* Radio indicator */}
            <div
              className={cn(
                'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200',
                !isSelected && !showFeedback && 'border-gray-300',
                isSelected && !showFeedback && 'border-guild-green bg-guild-green',
                isCorrectOption && 'border-emerald-500 bg-emerald-500',
                isIncorrectSelected && 'border-red-500 bg-red-500',
                showFeedback && !isCorrectOption && !isIncorrectSelected && 'border-gray-200',
              )}
            >
              {/* Inner indicators */}
              {isSelected && !showFeedback && (
                <div className="h-2 w-2 rounded-full bg-white" />
              )}
              {isCorrectOption && (
                <CheckCircle2 size={14} strokeWidth={2.5} className="text-white" />
              )}
              {isIncorrectSelected && (
                <XCircle size={14} strokeWidth={2.5} className="text-white" />
              )}
            </div>

            {/* Option text */}
            <span className="flex-1 leading-snug">{option}</span>
          </button>
        );
      })}
    </div>
  );
}

// ============================================
// T/F Buttons
// ============================================

function TrueFalseButtons({
  selectedAnswer,
  showFeedback,
  correctAnswer,
  onAnswer,
}: {
  selectedAnswer: string | null;
  showFeedback: boolean;
  correctAnswer?: string;
  onAnswer: (answer: string) => void;
}) {
  const buttons = ['True', 'False'] as const;

  return (
    <div className="grid grid-cols-2 gap-3">
      {buttons.map((value) => {
        const isSelected = selectedAnswer === value;
        const isCorrectOption = showFeedback && value === correctAnswer;
        const isIncorrectSelected = showFeedback && isSelected && value !== correctAnswer;
        const disabled = showFeedback;

        return (
          <button
            key={value}
            type="button"
            aria-label={`${value}`}
            aria-pressed={isSelected}
            disabled={disabled}
            onClick={() => !disabled && onAnswer(value)}
            className={cn(
              'flex min-h-[64px] items-center justify-center rounded-2xl px-6 py-4',
              'text-base font-semibold transition-all duration-200',
              'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
              // Default state
              !isSelected && !showFeedback && 'bg-white ring-1 ring-gray-200 text-op-text-primary active:ring-2 active:ring-guild-green/50 active:scale-[0.98]',
              // Selected (before feedback)
              isSelected && !showFeedback && 'ring-2 ring-guild-green bg-guild-green/5 text-guild-green',
              // Correct (feedback)
              isCorrectOption && 'ring-2 ring-emerald-500 bg-emerald-50 text-emerald-700',
              // Incorrect selected (feedback)
              isIncorrectSelected && 'ring-2 ring-red-500 bg-red-50 text-red-700',
              // Unselected during feedback (dim)
              showFeedback && !isCorrectOption && !isIncorrectSelected && 'bg-white ring-1 ring-gray-200 text-op-text-tertiary opacity-60',
              // Disabled cursor
              disabled && 'cursor-default',
            )}
          >
            <div className="flex items-center gap-2">
              {isCorrectOption && <CheckCircle2 size={18} strokeWidth={2} className="text-emerald-600" />}
              {isIncorrectSelected && <XCircle size={18} strokeWidth={2} className="text-red-600" />}
              {value}
            </div>
          </button>
        );
      })}
    </div>
  );
}
